// ===== Chat timeline UI surfaces =====
// 검색/인용/고정/날짜/최근 이동은 Phase 3 ChatTimelineNavigation target loader로 수렴한다.

type ChatPinnedTarget = { type: "message"; seq?: number; seqHint?: number } | { type: "artifact"; artifactId?: string; seqHint?: number };
type ChatPinnedItem = {
  id: string;
  target: ChatPinnedTarget;
  status?: "available" | "unavailable";
  source?: {
    type: "message";
    message?: { seq?: number; content?: string; timestamp?: string; senderName?: string | null };
  } | {
    type: "artifact";
    // 패널 가드(artifact-panel.ts#isArtifactPanelArtifact)가 요구하는 형상 그대로 통과시킨다 —
    // 서버 projectPinnedItemSource 가 projectForClient 결과를 통째로 내려주므로 재조회가 필요 없다.
    artifact?: { id?: string; title?: string; display?: string; kind?: string; payload?: Record<string, unknown> };
  };
};
type ChatWindowResolveResult = { ok: boolean; reason?: "bad-request" | "not-found" | "no-result" | "network"; targetSeq?: number; stale?: boolean };
type ChatSurfaceRequestToken = { sessionId: string; generation: number; signal?: AbortSignal };

interface ChatTimelineNavigationRuntime {
  loadTargetWindow(sessionId: string, targetSeq: number): Promise<boolean>;
  releaseAnchor(): void;
  currentAnchorSeq(): number | null;
  oldestRetainedSeq(): number | null;
}
interface Window {
  ChatTimelineSurfaces?: ChatTimelineSurfacesApi;
  currentSessionId?: string | null;
}
interface ChatTimelineSurfacesApi {
  jumpToSeq(sessionId: string, seq: number): Promise<boolean>;
  jumpToArtifact(sessionId: string, artifactId: string): Promise<boolean>;
  jumpToDate(sessionId: string, value: string): Promise<ChatWindowResolveResult>;
  returnToRecent(): boolean;
  loadOlderMessages(sessionId: string | null | undefined): boolean;
  refreshPinnedItems(sessionId?: string | null): Promise<void>;
  renderPinnedItems(items: ChatPinnedItem[]): void;
  openPinnedItem(sessionId: string, pinId: string): Promise<boolean>;
  syncDateDividers(): void;
  pinMessage(seq: number): Promise<void>;
  pinArtifact(artifactId: string): Promise<void>;
}

const CHAT_TIMELINE_FLASH_MS = 1200;
const CHAT_SELECTOR_ESCAPE_PATTERN = new RegExp("[\"\\\\]", "g");

function chatSurfaceRoot(): HTMLElement | null {
  const el = document.getElementById("chatMessages");
  return el instanceof HTMLElement ? el : null;
}

function chatSurfaceSessionId(sessionId?: string | null): string | null {
  return typeof sessionId === "string" && sessionId ? sessionId : typeof window.currentSessionId === "string" ? window.currentSessionId : null;
}

function escapeChatSelector(value: string): string {
  return typeof CSS !== "undefined" && typeof CSS.escape === "function"
    ? CSS.escape(value)
    : value.replace(CHAT_SELECTOR_ESCAPE_PATTERN, "\\$&");
}

function flashChatTarget(node: HTMLElement): void {
  node.scrollIntoView({ block: "center" });
  node.classList.add("flash");
  window.setTimeout(() => node.classList.remove("flash"), CHAT_TIMELINE_FLASH_MS);
}

function findRenderedSeq(sessionId: string, seq: number): HTMLElement | null {
  const root = chatSurfaceRoot();
  if (!root) return null;
  return root.querySelector<HTMLElement>(`.message[data-session-id="${escapeChatSelector(sessionId)}"][data-msg-seq="${escapeChatSelector(String(seq))}"]`);
}

function findRenderedArtifact(sessionId: string, artifactId: string): HTMLElement | null {
  const root = chatSurfaceRoot();
  if (!root) return null;
  const card = root.querySelector<HTMLElement>(`.artifact-card[data-artifact-id="${escapeChatSelector(artifactId)}"]`);
  const message = card?.closest<HTMLElement>(".message[data-session-id]");
  return message?.dataset.sessionId === sessionId ? message : null;
}

// 고정·고정 해제는 확인 모달을 거친다. 확인 수단(index.html 로드 순서상 항상 있다)이 없으면
// 묻지 않고 실행하느니 아무 일도 하지 않는다(business-document-card.ts _confirmIrreversible 선례).
function confirmPinAction(title: string, body: string, confirmLabel: string, onConfirm: () => Promise<void>): void {
  if (typeof showConfirmModal !== "function") {
    console.error("[chat-timeline-surfaces] 확인 모달 유틸이 없어 고정 변경을 진행하지 않는다");
    return;
  }
  showConfirmModal({ title, body, confirmLabel, onConfirm });
}

function timelineNavigation(): ChatTimelineNavigationRuntime | null {
  try {
    return (0, eval)("typeof chatTimelineNavigation !== \"undefined\" ? chatTimelineNavigation : null") as ChatTimelineNavigationRuntime | null;
  } catch {
    return null;
  }
}

function pinnedRail(): HTMLElement | null {
  const el = document.getElementById("chatPinnedRail");
  return el instanceof HTMLElement ? el : null;
}

function pinnedStatus(): HTMLElement | null {
  const el = document.getElementById("chatPinnedStatus");
  return el instanceof HTMLElement ? el : null;
}

function pinnedList(): HTMLElement | null {
  const el = document.getElementById("chatPinnedList");
  return el instanceof HTMLElement ? el : null;
}

function pinnedStrip(): HTMLElement | null {
  const el = document.getElementById("chatPinnedStrip");
  return el instanceof HTMLElement ? el : null;
}

function pinnedLatest(): HTMLElement | null {
  const el = document.getElementById("chatPinnedLatest");
  return el instanceof HTMLElement ? el : null;
}

function chatDateStatus(): HTMLElement | null {
  const el = document.getElementById("chatDateStatus");
  return el instanceof HTMLElement ? el : null;
}

function setPinnedOpen(open: boolean): void {
  const rail = pinnedRail();
  if (rail) rail.dataset.open = String(open);
  pinnedStrip()?.setAttribute("aria-expanded", String(open));
}

function isPinnedOpen(): boolean {
  return pinnedRail()?.dataset.open === "true";
}

// 표시 규칙: ready/error 만 띠를 띄우고, empty 는 띠 자체를 숨긴다(고정이 없는 방에서 빈 줄이
// 자리를 먹지 않게 하는 계약). loading 은 현재 표시 상태를 유지한다 — 방을 옮길 때마다 아직
// 개수를 모르는 띠가 깜빡였다 사라지는 걸 막는다.
function setPinnedState(state: "loading" | "empty" | "ready" | "error", text: string): void {
  const rail = pinnedRail();
  const status = pinnedStatus();
  if (rail) {
    rail.dataset.state = state;
    if (state === "ready" || state === "error") rail.hidden = false;
    else if (state === "empty") {
      rail.hidden = true;
      setPinnedOpen(false);
    }
  }
  if (status) status.textContent = text;
}

function setPinnedLatest(text: string): void {
  const latest = pinnedLatest();
  if (latest) latest.textContent = text;
}

function pinnedTitle(item: ChatPinnedItem): string {
  if (isPinnedUnavailable(item)) return item.target.type === "artifact" ? "아티팩트 근거 없음" : "메시지 근거 없음";
  if (item.source?.type === "artifact") return item.source.artifact?.title || "아티팩트";
  if (item.source?.type === "message") return item.source.message?.senderName || "메시지";
  return "근거";
}

function pinnedSnippet(item: ChatPinnedItem): string {
  if (isPinnedUnavailable(item)) return "원문을 사용할 수 없어요.";
  if (item.source?.type === "message") return item.source.message?.content || "";
  if (item.source?.type === "artifact") return item.source.artifact?.display || item.source.artifact?.kind || "artifact";
  return "";
}

function pinnedSummary(item: ChatPinnedItem): string {
  const snippet = pinnedSnippet(item).replace(/\s+/g, " ").trim();
  const title = pinnedTitle(item);
  return snippet ? `${title} \u00B7 ${snippet}` : title;
}

function isPinnedUnavailable(item: ChatPinnedItem): boolean {
  return item.status === "unavailable";
}

function targetSeqFromPinned(item: ChatPinnedItem): number | null {
  const target = item.target;
  const seq = target.type === "message" ? target.seq ?? target.seqHint : target.seqHint;
  return Number.isInteger(seq) && (seq as number) >= 0 ? seq as number : null;
}

function createPinnedItemNode(item: ChatPinnedItem): HTMLElement {
  const row = document.createElement("button");
  row.type = "button";
  row.className = "chat-pinned-item";
  row.dataset.pinId = item.id;
  row.setAttribute("role", "listitem");
  const seq = targetSeqFromPinned(item);
  const unavailable = isPinnedUnavailable(item);
  if (seq !== null) row.dataset.seq = String(seq);
  if (item.target.type === "artifact" && item.target.artifactId) row.dataset.artifactId = item.target.artifactId;
  if (unavailable) row.dataset.unavailable = "true";
  row.setAttribute("aria-disabled", String(unavailable));

  const kind = document.createElement("span");
  kind.className = "chat-pinned-item-kind";
  kind.setAttribute("aria-hidden", "true");
  kind.textContent = item.target.type === "artifact" ? "\u{1F4C4}" : "\u{1F4AC}";

  const main = document.createElement("span");
  main.className = "chat-pinned-item-main";
  const label = document.createElement("span");
  label.className = "chat-pinned-item-label";
  label.textContent = pinnedTitle(item);
  const snippet = document.createElement("span");
  snippet.className = "chat-pinned-item-snippet";
  snippet.textContent = pinnedSnippet(item);
  main.append(label, snippet);

  const state = document.createElement("span");
  state.className = "chat-pinned-source-state";
  state.textContent = unavailable ? "unavailable" : item.source?.type || item.target.type;
  row.append(kind, main, state);

  const remove = document.createElement("span");
  remove.className = "chat-pin-remove";
  remove.dataset.pinId = item.id;
  remove.setAttribute("role", "button");
  remove.setAttribute("tabindex", "0");
  remove.setAttribute("aria-label", "고정 해제");
  remove.textContent = "×";
  row.appendChild(remove);
  return row;
}

function localMidnightRange(value: string): { start: string; end: string } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const start = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(start.getTime())) return null;
  const end = new Date(start);
  end.setDate(start.getDate() + 1);
  return { start: start.toISOString(), end: end.toISOString() };
}

async function resolveWindowTarget(sessionId: string, params: URLSearchParams, signal?: AbortSignal): Promise<ChatWindowResolveResult> {
  try {
    const response = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/messages/window?${params.toString()}`, signal ? { signal } : undefined);
    if (response.status === 400) return { ok: false, reason: "bad-request" };
    if (response.status === 404) return { ok: false, reason: "not-found" };
    if (!response.ok) return { ok: false, reason: "network" };
    const body = await response.json();
    const targetSeq = Number(body?.targetSeq);
    return body?.ok === true && body.sessionId === sessionId && Number.isInteger(targetSeq) && targetSeq >= 0
      ? { ok: true, targetSeq }
      : { ok: false, reason: "no-result" };
  } catch {
    return { ok: false, reason: "network" };
  }
}

class ChatTimelineSurfacesController implements ChatTimelineSurfacesApi {
  private dividerFrame = 0;
  private targetGeneration = 0;
  private targetController: AbortController | null = null;
  private pinnedGeneration = 0;
  private pinnedById = new Map<string, ChatPinnedItem>();

  async jumpToSeq(sessionId: string, seq: number): Promise<boolean> {
    if (!sessionId || !Number.isInteger(seq) || seq < 0) return false;
    const token = this.beginTargetRequest(sessionId);
    if (!token) return false;
    return this.jumpToSeqWithToken(token, seq);
  }

  private async jumpToSeqWithToken(token: ChatSurfaceRequestToken, seq: number): Promise<boolean> {
    const sessionId = token.sessionId;
    if (!this.isTargetCurrent(token) || !Number.isInteger(seq) || seq < 0) return false;
    const rendered = findRenderedSeq(sessionId, seq);
    if (rendered) {
      if (!this.isTargetCurrent(token)) return false;
      flashChatTarget(rendered);
      return true;
    }
    if (!this.isTargetCurrent(token)) return false;
    const loaded = await timelineNavigation()?.loadTargetWindow(sessionId, seq);
    if (!this.isTargetCurrent(token)) return false;
    const target = loaded ? findRenderedSeq(sessionId, seq) : null;
    if (target) flashChatTarget(target);
    if (this.isTargetCurrent(token)) this.scheduleDateDividers();
    return !!target;
  }

  // 스크롤업 좌표 소유자. load-more 의 offset 은 "최신 기준 거리"라 점프로 화면 상단이 tail 에서
  // 떨어져 나오면 그 위를 가리키지 못한다(점프 뒤 스크롤업이 엉뚱한 구간을 불러오는 원인).
  // anchor 가 살아 있다 == tail 좌표계가 유효하지 않다 는 뜻이므로, 그때만 절대 seq 창으로 이어받는다.
  // 창이 S-25..S+24 이므로 S = 최상단-25 면 정확히 그 위 50건이 온다. 겹치는 구간은 canonical key
  // 병합이라 중복 렌더되지 않는다. 처리했으면 true 를 돌려 호출부가 기존 offset 경로를 타지 않게 한다.
  loadOlderMessages(sessionId: string | null | undefined): boolean {
    const nav = timelineNavigation();
    if (!nav || !sessionId || typeof nav.currentAnchorSeq !== "function") return false;
    if (nav.currentAnchorSeq() === null) return false;

    const oldest = nav.oldestRetainedSeq();
    if (oldest === null) return false;
    if (oldest <= 0) {
      // 파일 첫 줄까지 왔다. 더 볼 게 없으니 게이트만 닫는다.
      messageListState.hasMore = false;
      messageListState.loading = false;
      return true;
    }

    void nav.loadTargetWindow(sessionId, Math.max(0, oldest - 25))
      .then((ok) => { if (!ok) messageListState.loading = false; })
      .catch(() => { messageListState.loading = false; });
    return true;
  }

  async jumpToArtifact(sessionId: string, artifactId: string): Promise<boolean> {
    if (!sessionId || !artifactId) return false;
    const token = this.beginTargetRequest(sessionId);
    if (!token) return false;
    const rendered = findRenderedArtifact(sessionId, artifactId);
    if (rendered?.dataset.msgSeq) return this.jumpToSeqWithToken(token, Number(rendered.dataset.msgSeq));
    const resolved = await resolveWindowTarget(sessionId, new URLSearchParams({ artifactId }), token.signal);
    if (!this.isTargetCurrent(token)) return false;
    return resolved.ok && resolved.targetSeq !== undefined ? this.jumpToSeqWithToken(token, resolved.targetSeq) : false;
  }

  async jumpToDate(sessionId: string, value: string): Promise<ChatWindowResolveResult> {
    if (!sessionId) return { ok: false, reason: "no-result" };
    const range = localMidnightRange(value);
    if (!range) return { ok: false, reason: "bad-request" };
    const token = this.beginTargetRequest(sessionId);
    if (!token) return { ok: false, reason: "no-result", stale: true };
    const resolved = await resolveWindowTarget(sessionId, new URLSearchParams({ dateStart: range.start, dateEnd: range.end }), token.signal);
    if (!this.isTargetCurrent(token)) return { ok: false, reason: "no-result", stale: true };
    if (!resolved.ok || resolved.targetSeq === undefined) return resolved;
    const loaded = await this.jumpToSeqWithToken(token, resolved.targetSeq);
    if (!this.isTargetCurrent(token)) return { ok: false, reason: "no-result", stale: true };
    return loaded ? resolved : { ok: false, reason: "no-result" };
  }

  returnToRecent(): boolean {
    this.cancelTargetRequest();
    timelineNavigation()?.releaseAnchor();
    this.syncDateDividers();
    const root = chatSurfaceRoot();
    if (root) {
      root.scrollTop = root.scrollHeight;
      if (!root.hasAttribute("tabindex")) root.setAttribute("tabindex", "-1");
      root.focus({ preventScroll: true });
    }
    this.scheduleDateDividers();
    return true;
  }

  async refreshPinnedItems(sessionId?: string | null): Promise<void> {
    const current = chatSurfaceSessionId(sessionId);
    if (!current || !this.isSessionCurrent(current)) return;
    const token = this.beginPinnedRequest(current);
    setPinnedState("loading", "고정 불러오는 중");
    try {
      const response = await fetch(`/api/sessions/${encodeURIComponent(current)}/pinned-items`);
      const body = await response.json();
      if (!this.isPinnedCurrent(token)) return;
      if (!response.ok || body?.ok !== true) throw new Error("load failed");
      this.renderPinnedItems(Array.isArray(body.items) ? body.items as ChatPinnedItem[] : []);
    } catch {
      if (this.isPinnedCurrent(token)) setPinnedState("error", "고정을 불러오지 못했어요");
    }
  }

  renderPinnedItems(items: ChatPinnedItem[]): void {
    const list = pinnedList();
    if (!list) return;
    list.textContent = "";
    this.pinnedById.clear();
    if (!items.length) {
      setPinnedLatest("");
      setPinnedState("empty", "고정 없음");
      return;
    }
    for (const item of items) {
      this.pinnedById.set(item.id, item);
      list.appendChild(createPinnedItemNode(item));
    }
    setPinnedLatest(pinnedSummary(items[0]));
    setPinnedState("ready", `${items.length}개 고정`);
  }

  // 고정 항목 클릭의 기본 동작은 "점프"가 아니라 "미리보기"다 — 근거를 확인하는 것과 그 맥락으로
  // 돌아가는 것은 다른 의도라, 후자는 패널 안 '대화에서 보기'(onJump)로 분리했다.
  // 패널 스크립트가 없는 환경에서는 기존 점프로 물러선다.
  async openPinnedItem(sessionId: string, pinId: string): Promise<boolean> {
    const item = this.pinnedById.get(pinId);
    if (!sessionId || !item || isPinnedUnavailable(item)) return false;
    setPinnedOpen(false);
    if (this.previewPinnedItem(sessionId, item)) return true;
    return this.jumpToPinnedItem(sessionId, item);
  }

  private previewPinnedItem(sessionId: string, item: ChatPinnedItem): boolean {
    const panel = window.ArtifactPanel;
    if (!panel) return false;
    const onJump = () => { void this.jumpToPinnedItem(sessionId, item); };
    if (item.source?.type === "artifact" && item.source.artifact && typeof panel.open === "function") {
      panel.open(item.source.artifact, null, { onJump });
      return true;
    }
    if (item.source?.type === "message" && item.source.message && typeof panel.openMessage === "function") {
      panel.openMessage(item.source.message, { onJump });
      return true;
    }
    return false;
  }

  // seq 우선, 없으면 artifactId — 개편 전 클릭 핸들러의 순서를 그대로 유지한다.
  private jumpToPinnedItem(sessionId: string, item: ChatPinnedItem): Promise<boolean> {
    const seq = targetSeqFromPinned(item);
    if (seq !== null) return this.jumpToSeq(sessionId, seq);
    const artifactId = item.target.type === "artifact" ? item.target.artifactId : "";
    return artifactId ? this.jumpToArtifact(sessionId, artifactId) : Promise.resolve(false);
  }

  syncDateDividers(): void {
    const root = chatSurfaceRoot();
    if (!root) return;
    const expected: Array<{ message: HTMLElement; label: string }> = [];
    let currentDate = "";
    for (const message of Array.from(root.querySelectorAll<HTMLElement>(".message[data-msg-seq]"))) {
      const label = (message.querySelector<HTMLElement>(".message-time")?.textContent || "").trim().slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(label) || label === currentDate) continue;
      currentDate = label;
      expected.push({ message, label });
    }
    const existing = Array.from(root.querySelectorAll(".chat-date-divider"));
    // 자신의 DOM 변경을 관찰한 다음 프레임에서 또 제거·삽입하는 루프를 막는다.
    if (existing.length === expected.length && expected.every(({ message, label }, index) =>
      existing[index].textContent === label && existing[index].nextElementSibling === message)) return;
    const follow = root.scrollHeight - root.scrollTop - root.clientHeight < 4;
    const top = root.scrollTop;
    const bounds = root.getBoundingClientRect();
    const anchor = Array.from(root.children).find((node): node is HTMLElement =>
      node instanceof HTMLElement && !node.classList.contains("chat-date-divider")
      && node.getBoundingClientRect().bottom > bounds.top);
    const anchorTop = anchor?.offsetTop;
    existing.forEach((node) => node.remove());
    for (const { message, label } of expected) {
      const divider = document.createElement("div");
      divider.className = "chat-date-divider";
      divider.setAttribute("role", "separator");
      divider.setAttribute("aria-label", `${label} 대화`);
      divider.textContent = label;
      root.insertBefore(divider, message);
    }
    if (follow) root.scrollTop = root.scrollHeight;
    else if (anchor?.parentElement === root && anchorTop !== undefined) root.scrollTop = top + anchor.offsetTop - anchorTop;
  }

  async pinMessage(seq: number): Promise<void> {
    const sessionId = chatSurfaceSessionId();
    if (!sessionId || !Number.isInteger(seq) || seq < 0) return;
    await this.createPin(sessionId, { type: "message", seq });
  }

  async pinArtifact(artifactId: string): Promise<void> {
    const sessionId = chatSurfaceSessionId();
    if (!sessionId || !artifactId) return;
    await this.createPin(sessionId, { type: "artifact", artifactId });
  }

  scheduleDateDividers(): void {
    if (this.dividerFrame) return;
    this.dividerFrame = window.requestAnimationFrame(() => {
      this.dividerFrame = 0;
      this.syncDateDividers();
    });
  }

  private async createPin(sessionId: string, target: ChatPinnedTarget): Promise<void> {
    const response = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/pinned-items`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ target }),
    });
    const body = await response.json().catch(() => null);
    if (!response.ok || body?.ok !== true) throw new Error("pin failed");
    await this.refreshPinnedItems(sessionId);
  }

  async removePin(sessionId: string, pinId: string): Promise<void> {
    await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/pinned-items/${encodeURIComponent(pinId)}`, { method: "DELETE" });
    await this.refreshPinnedItems(sessionId);
  }

  private beginTargetRequest(sessionId: string): ChatSurfaceRequestToken | null {
    if (!this.isSessionCurrent(sessionId)) return null;
    this.cancelTargetRequest();
    this.targetGeneration += 1;
    const controller = new AbortController();
    this.targetController = controller;
    return { sessionId, generation: this.targetGeneration, signal: controller.signal };
  }

  private cancelTargetRequest(): void {
    this.targetController?.abort();
    this.targetController = null;
    this.targetGeneration += 1;
  }

  private isTargetCurrent(token: ChatSurfaceRequestToken): boolean {
    return this.targetGeneration === token.generation
      && this.isSessionCurrent(token.sessionId)
      && token.signal?.aborted !== true;
  }

  private beginPinnedRequest(sessionId: string): ChatSurfaceRequestToken {
    this.pinnedGeneration += 1;
    return { sessionId, generation: this.pinnedGeneration };
  }

  private isPinnedCurrent(token: ChatSurfaceRequestToken): boolean {
    return this.pinnedGeneration === token.generation && this.isSessionCurrent(token.sessionId);
  }

  private isSessionCurrent(sessionId: string): boolean {
    return chatSurfaceSessionId() === sessionId;
  }
}

const chatTimelineSurfaces = new ChatTimelineSurfacesController();
window.ChatTimelineSurfaces = chatTimelineSurfaces;

function installTimelineSurfaceHooks(): void {
  const nav = timelineNavigation() as (ChatTimelineNavigationRuntime & {
    __surfaceHooksInstalled?: boolean;
    resetToHistory?: (...args: unknown[]) => boolean;
    prependHistory?: (...args: unknown[]) => boolean;
    registerRenderedNode?: (...args: unknown[]) => HTMLElement | null | undefined;
  }) | null;
  if (!nav || nav.__surfaceHooksInstalled) return;
  const resetToHistory = nav.resetToHistory?.bind(nav);
  if (resetToHistory) {
    nav.resetToHistory = (...args: unknown[]) => {
      const accepted = resetToHistory(...args);
      if (accepted) {
        const data = args[0] as { sessionId?: unknown } | undefined;
        void chatTimelineSurfaces.refreshPinnedItems(typeof data?.sessionId === "string" ? data.sessionId : null);
        chatTimelineSurfaces.scheduleDateDividers();
      }
      return accepted;
    };
  }
  const prependHistory = nav.prependHistory?.bind(nav);
  if (prependHistory) {
    nav.prependHistory = (...args: unknown[]) => {
      const accepted = prependHistory(...args);
      if (accepted) chatTimelineSurfaces.scheduleDateDividers();
      return accepted;
    };
  }
  const registerRenderedNode = nav.registerRenderedNode?.bind(nav);
  if (registerRenderedNode) {
    nav.registerRenderedNode = (...args: unknown[]) => {
      const node = registerRenderedNode(...args);
      chatTimelineSurfaces.scheduleDateDividers();
      return node;
    };
  }
  nav.__surfaceHooksInstalled = true;
}

installTimelineSurfaceHooks();

// 배선은 한 번만 한다. installTimelineSurfaceHooks 의 __surfaceHooksInstalled 와 같은 이유로,
// DOMContentLoaded 가 두 번 도달하는 환경(문서 파싱 이벤트와 수동 dispatch 가 겹치는 경우)에서
// 리스너가 중복 등록되면 클릭 한 번이 핸들러를 여러 번 실행한다.
let chatSurfaceDomWired = false;

document.addEventListener("DOMContentLoaded", () => {
  if (chatSurfaceDomWired) return;
  chatSurfaceDomWired = true;
  const root = chatSurfaceRoot();
  if (root && !root.hasAttribute("tabindex")) root.setAttribute("tabindex", "-1");

  document.getElementById("chatDateJumpBtn")?.addEventListener("click", () => {
    const input = document.getElementById("chatDateInput");
    if (!(input instanceof HTMLInputElement)) return;
    if (typeof input.showPicker === "function") input.showPicker();
    else {
      input.focus();
      input.click();
    }
  });

  document.getElementById("chatDateInput")?.addEventListener("change", async (event) => {
    const input = event.target instanceof HTMLInputElement ? event.target : null;
    const sessionId = chatSurfaceSessionId();
    const status = chatDateStatus();
    if (!input || !sessionId) return;
    const result = await chatTimelineSurfaces.jumpToDate(sessionId, input.value);
    if (result.stale || chatSurfaceSessionId() !== sessionId) return;
    if (status) {
      status.textContent = result.ok ? "날짜 위치로 이동했어요"
        : result.reason === "bad-request" ? "날짜 요청이 올바르지 않아요"
        : result.reason === "not-found" ? "해당 날짜 대화를 찾지 못했어요"
        : result.reason === "network" ? "날짜 위치를 불러오지 못했어요"
        : "해당 날짜 대화가 없어요";
    }
  });

  document.getElementById("chatRecentBtn")?.addEventListener("click", () => {
    chatTimelineSurfaces.returnToRecent();
    if (typeof hideNewMessageFloating === "function") hideNewMessageFloating();
  });

  pinnedList()?.addEventListener("click", async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const sessionId = chatSurfaceSessionId();
    if (!target || !sessionId) return;
    const remove = target.closest<HTMLElement>(".chat-pin-remove");
    if (remove?.dataset.pinId) {
      event.preventDefault();
      event.stopPropagation();
      const pinId = remove.dataset.pinId;
      confirmPinAction("고정 해제", "이 항목의 고정을 해제할까요?", "해제", () => chatTimelineSurfaces.removePin(sessionId, pinId));
      return;
    }
    const row = target.closest<HTMLElement>(".chat-pinned-item");
    if (!row || row.dataset.unavailable === "true" || !row.dataset.pinId) return;
    await chatTimelineSurfaces.openPinnedItem(sessionId, row.dataset.pinId);
  });

  // 접힘/펼침. 정밀 포인터에서는 hover 로 열고, 터치에서는 탭 토글만 남긴다(hover 가 없다).
  const finePointer = typeof window.matchMedia === "function"
    ? window.matchMedia("(hover: hover) and (pointer: fine)")
    : null;
  const rail = pinnedRail();
  rail?.addEventListener("mouseenter", () => { if (finePointer?.matches) setPinnedOpen(true); });
  rail?.addEventListener("mouseleave", () => { if (finePointer?.matches) setPinnedOpen(false); });
  pinnedStrip()?.addEventListener("click", () => setPinnedOpen(!isPinnedOpen()));

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || !isPinnedOpen()) return;
    setPinnedOpen(false);
    pinnedStrip()?.focus();
  });

  document.addEventListener("click", (event) => {
    if (!isPinnedOpen()) return;
    const target = event.target instanceof Element ? event.target : null;
    if (target && rail?.contains(target)) return;
    setPinnedOpen(false);
  });

  pinnedList()?.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (!target?.matches(".chat-pinned-item, .chat-pin-remove")) return;
    event.preventDefault();
    target.click();
  });

  root?.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const pinButton = target.closest<HTMLElement>('.msg-act[data-act="pin"]');
    const messageEl = pinButton?.closest<HTMLElement>(".message");
    if (!pinButton || !messageEl) return;
    // 아티팩트 메시지의 📌는 메시지 seq가 아니라 아티팩트 자체를 고정한다(서버 target.type "artifact").
    const artifactId = messageEl.querySelector<HTMLElement>(".artifact-card")?.getAttribute("data-artifact-id") || "";
    if (artifactId) {
      confirmPinAction("아티팩트 고정", "이 아티팩트를 상단에 고정할까요?", "고정", () => chatTimelineSurfaces.pinArtifact(artifactId));
      return;
    }
    const seq = Number(messageEl.dataset.msgSeq);
    confirmPinAction("메시지 고정", "이 메시지를 상단에 고정할까요?", "고정", () => chatTimelineSurfaces.pinMessage(seq));
  });

  const observer = root ? new MutationObserver(() => chatTimelineSurfaces.scheduleDateDividers()) : null;
  if (root && observer) observer.observe(root, { childList: true });
  chatTimelineSurfaces.refreshPinnedItems();
  chatTimelineSurfaces.syncDateDividers();
});
